import React from 'react';
import {
    Avatar,
    AvatarSizes,
    AvatarShapes,
    Button,
    ButtonVariants,
    ButtonColors,
    ButtonFontWeights,
    Text,
    TextAs,
    TextSizes,
    Tooltip,
} from '@clockout-dev/design-elements';
import UserDetailSection from './UserDetailSection';
import UserDetailRow from './UserDetailRow';
import JourneyStageTag from './JourneyStageTag';
import DateTimeCell from './DateTimeCell';
import OpenSsoApp from '../../open-sso/OpenSsoApp';
import { describeSyncReport, getWorkTypeLabel } from './syncStatus.constants';
import { TIMEZONE_NOTE } from '../../../utils/datetime';
import SsoIdentityBar from '../../open-sso/elements/SsoIdentityBar';
import barStyles from '../../open-sso/elements/styles/sso-identity-bar.module.scss';
import { SSO_DISABLED_TOOLTIP } from '../../../constants/sso.constants';
import styles from './styles/user-detail-panel.module.scss';

/**
 * Side panel / drawer for the selected user (Figma node 5693:16860).
 *
 *  - Header: avatar, name, 3 action buttons (`Open SSO`, `View User`,
 *    `Restrict Access`). `Open SSO` always renders and is *disabled*
 *    without `admin:sso:open`, with a tooltip naming the permission,
 *    reachable by hover and by keyboard focus on the wrapper; the other
 *    two disappear without theirs. Deliberate: admin:sso:open is delegable,
 *    so an admin who cannot see it has no way to know to ask for it.
 *  - USER DETAILS: Journey Stage (same tag + vocabulary as the table),
 *    Last Transaction (absolute date/time), Payroll System, Work Type.
 *  - PAYROLL SYNC STATUS: the rollup pill (from the row's bulk rollup until
 *    the detail report loads, then from `report.rollup_status`) and one row
 *    per dimension, all from the single mapping in `syncStatus.constants`,
 *    each with a tooltip and an accessible name. The detail report is
 *    fetched only for the selected user; `syncDetail.status` drives a bounded
 *    loading state, an error state with Retry, or the loaded rows — nothing
 *    can sit at "Loading…" indefinitely.
 *  - ACCOUNT SERVICING: placeholder rows until the backend exposes them.
 *
 * @param {Object} props
 * @param {object | null} props.user row from the users list
 * @param {{ status: 'idle'|'loading'|'ready'|'error', report?: object|null, error?: string|null } | null} [props.syncDetail]
 * @param {() => void} [props.onRetrySync]
 */
function UserDetailPanel({
    user,
    syncDetail = null,
    onRetrySync,
    ssoBoot = null,
    onOpenSso,
    onExitSso,
    onViewUser,
    onRestrictUser,
    ssoLoading,
    restrictLoading,
    isRestricted,
    asModal = false,
    canRestrict = true,
    canOpenSso = false,
    canViewUser = true,
}) {
    if (!user) {
        return (
            <div className={styles.panel}>
                <div className={styles.emptyState}>
                    <Text as={TextAs.P} size={TextSizes.MEDIUM} className={styles.emptyTitle}>
                        Select a user
                    </Text>
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.emptyDescription}>
                        Click any row on the left to see their profile, payroll sync status and account servicing detail.
                    </Text>
                </div>
            </div>
        );
    }

    const wrapperClass = `${styles.panel} ${asModal ? styles.panelModal : ''}`.trim();
    const ssoActive = !!ssoBoot;

    const detailStatus = syncDetail?.status || 'idle';
    const report = detailStatus === 'ready' ? syncDetail?.report || null : null;
    const { rollup, dimensions, inconsistency } = describeSyncReport(report, {
        rollupFallback: user.syncStatus === null ? null : user.syncStatus?.status,
    });

    return (
        <div className={wrapperClass}>
            <div className={styles.headerStack}>
                <div className={styles.avatarHalo}>
                    <Avatar
                        name={user.name || 'Unknown user'}
                        size={AvatarSizes.LARGE}
                        shape={AvatarShapes.ROUNDED}
                        classNames={{
                            wrapper: styles.headerAvatarWrapper,
                            root: styles.headerAvatarRoot,
                            initials: styles.headerAvatarInitials,
                            label: styles.headerAvatarLabel,
                            title: styles.headerAvatarLabel,
                            subtitle: styles.headerAvatarLabel,
                        }}
                    />
                </div>

                <Text as={TextAs.P} size={TextSizes.MEDIUM} className={styles.userName}>
                    {user.name || 'Unknown user'}
                </Text>

                <div className={styles.actionsRow}>
                    {/* The span is both the Tooltip's anchor and the button's
                        layout slot: a natively-disabled <button> fires no
                        mouse events AND is out of the tab order, so the one
                        control with something to explain could otherwise be
                        neither hovered nor reached. `tabIndex` goes on only
                        when there is something to say — an enabled button is
                        already its own tab stop and the slot must not add a
                        second, dead one. */}
                    <Tooltip
                        title={canOpenSso ? '' : SSO_DISABLED_TOOLTIP}
                        trigger={['hover', 'focus']}
                    >
                        <span
                            className={styles.actionSlot}
                            data-testid="open-sso-slot"
                            tabIndex={canOpenSso ? undefined : 0}
                        >
                            <Button
                                variant={ButtonVariants.DEFAULT}
                                color={ButtonColors.PRIMARY}
                                fontWeight={ButtonFontWeights.BOLD}
                                onClick={() => onOpenSso?.(user.id)}
                                disabled={!canOpenSso || !!ssoLoading}
                                className={`${styles.actionButton} ${styles.actionButtonSso}`}
                            >
                                {ssoActive ? 'Close SSO' : 'Open SSO'}
                            </Button>
                        </span>
                    </Tooltip>
                    {canViewUser ? (
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.SECONDARY}
                            fontWeight={ButtonFontWeights.BOLD}
                            onClick={() => onViewUser?.(user.id)}
                            className={`${styles.actionButton} ${styles.actionButtonView}`}
                        >
                            View User
                        </Button>
                    ) : null}
                    {canRestrict ? (
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.LIGHT}
                            fontWeight={ButtonFontWeights.BOLD}
                            onClick={() => onRestrictUser?.(user.id, isRestricted)}
                            disabled={!!restrictLoading}
                            className={`${styles.actionButton} ${styles.actionButtonRestrict}`}
                        >
                            {isRestricted ? 'Unrestrict' : 'Restrict'} Access
                        </Button>
                    ) : null}
                </div>
            </div>

            {ssoActive ? (
                <div className={styles.ssoEmbedHost}>
                    {/* Host chrome, outside the RN tree: nothing the embedded
                        app renders or navigates to can remove it. */}
                    <SsoIdentityBar
                        identity={ssoBoot.identity}
                        fallbackName={user.name}
                        onExit={() => onExitSso?.(user.id)}
                    />
                    <div className={barStyles.hostContent}>
                        <OpenSsoApp key={ssoBoot.clientId} boot={ssoBoot} />
                    </div>
                </div>
            ) : (
            <>
            <UserDetailSection title="User Details">
                <UserDetailRow label="Journey Stage" value={<JourneyStageTag stage={user.journeyStage} />} />
                <UserDetailRow
                    label={(
                        <Tooltip title={`Most recent advance, settlement or fee in the ledger. ${TIMEZONE_NOTE}`} placement="top">
                            <span>Last Transaction</span>
                        </Tooltip>
                    )}
                    value={<DateTimeCell value={user.lastTransactionAt} />}
                />
                <UserDetailRow label="Access" value={isRestricted ? 'Restricted' : 'Active'} />
                <UserDetailRow label="Payroll System" value={user.payrollSystem || '—'} />
                <UserDetailRow label="Work Type" value={getWorkTypeLabel(report)} />
            </UserDetailSection>

            <UserDetailSection title="Payroll Sync Status">
                <div className={styles.rollupRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.dimLabel}>
                        Overall
                    </Text>
                    <Tooltip title={inconsistency ? `${rollup.description} ${inconsistency}` : rollup.description} placement="left">
                        <Text
                            as={TextAs.SPAN}
                            size={TextSizes.EXTRA_SMALL}
                            className={`${styles.pill} ${styles[`pill_${rollup.tone}`]}`}
                            role="status"
                            aria-label={`Sync status: ${rollup.label}`}
                        >
                            {rollup.label}
                        </Text>
                    </Tooltip>
                </div>
                {inconsistency ? (
                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.inconsistencyNote}>
                        {inconsistency}
                    </Text>
                ) : null}

                {detailStatus === 'loading' ? (
                    <div className={styles.syncStateRow} role="status" aria-live="polite">
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.syncStateText}>
                            Loading payroll dimensions…
                        </Text>
                    </div>
                ) : null}

                {detailStatus === 'error' ? (
                    <div className={styles.syncStateRow} role="alert">
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.syncStateError}>
                            {syncDetail?.error === 'timeout'
                                ? 'Payroll dimensions took too long to load.'
                                : "Couldn't load payroll dimensions."}
                        </Text>
                        <Button
                            variant={ButtonVariants.DEFAULT}
                            color={ButtonColors.SECONDARY}
                            onClick={() => onRetrySync?.(user.id)}
                            className={styles.syncRetryButton}
                        >
                            Retry
                        </Button>
                    </div>
                ) : null}

                {detailStatus === 'ready' ? dimensions.map(({ key, label, state }) => (
                    <div key={key} className={styles.dimRow}>
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.dimLabel}>
                            {label}
                        </Text>
                        <Tooltip title={state.description} placement="left">
                            {!state.applicable ? (
                                <Text
                                    as={TextAs.SPAN}
                                    size={TextSizes.SMALL}
                                    className={styles.dimValueNa}
                                    aria-label={`${label}: ${state.label}`}
                                >
                                    {state.label}
                                </Text>
                            ) : (
                                <Text
                                    as={TextAs.SPAN}
                                    size={TextSizes.EXTRA_SMALL}
                                    className={`${styles.pill} ${styles[`pill_${state.tone}`]}`}
                                    role="status"
                                    aria-label={`${label}: ${state.label}`}
                                >
                                    {state.label}
                                </Text>
                            )}
                        </Tooltip>
                    </div>
                )) : null}
            </UserDetailSection>

            <UserDetailSection title="Account Servicing">
                {/* TODO(backend): expose servicing state + resolution
                    on the user / sync payload. Placeholders until then. */}
                <UserDetailRow label="State" value={user.servicingState || 'N/A'} />
                <UserDetailRow label="Resolution" value={user.servicingResolution || 'Link T&A'} />
            </UserDetailSection>
            </>
            )}
        </div>
    );
}

export default UserDetailPanel;
